import {
  faCircleHalfStroke,
  faMoon,
  faSun,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// import i18next, { t } from "i18next";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const LOCKED_THEME: Theme | null = "dark";

export type Theme = "system" | "light" | "dark";
export const Themes: Theme[] = ["system", "light", "dark"];

export function getComputed(element: HTMLElement, property: string): string {
  return getComputedStyle(element).getPropertyValue(property).trim();
}

export function useThemeChange(callback: () => void) {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    const observer = new MutationObserver(() => {
      callbackRef.current();
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  }, []);
}

export function systemIsDark() {
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

export function applyTheme(theme: string) {
  if (theme === "system") {
    const isDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
    document.documentElement.setAttribute(
      "data-theme",
      isDark ? "dark" : "light",
    );
  } else {
    document.documentElement.setAttribute("data-theme", theme);
  }
  localStorage.setItem("theme", theme);
}

export function incrementTheme() {
  const current = (localStorage.getItem("theme") as Theme) || "system";
  const nextIndex = (Themes.indexOf(current) + 1) % Themes.length;
  applyTheme(Themes[nextIndex] ?? "system");
}

function Theme() {
  useEffect(() => {
    if (LOCKED_THEME !== null) {
      applyTheme(LOCKED_THEME);
      return;
    }
    const stored = (localStorage.getItem("theme") as Theme | null) || "system";
    applyTheme(stored);

    const onStorage = (e: StorageEvent) => {
      if (e.key === "theme" && e.newValue) {
        applyTheme(e.newValue);
      }
    };
    window.addEventListener("storage", onStorage);

    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const onSystemThemeChange = () => {
      const current =
        (localStorage.getItem("theme") as Theme | null) || "system";
      if (current === "system") {
        applyTheme("system");
      }
    };
    media.addEventListener("change", onSystemThemeChange);

    return () => {
      window.removeEventListener("storage", onStorage);
      media.removeEventListener("change", onSystemThemeChange);
    };
  }, []);
  return null;
}

export function ThemeSwitcher() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const [theme, setTheme] = useState<Theme>(() => {
    if (typeof window !== "undefined") {
      return (localStorage.getItem("theme") as Theme) || "system";
    }
    return "system";
  });
  useThemeChange(() => {
    setTheme((localStorage.getItem("theme") as Theme) || "system");
  });

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    const onStorage = (e: StorageEvent) => {
      if (e.key === "theme" && e.newValue) {
        setTheme(e.newValue as Theme);
      }
    };
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const onSystemThemeChange = () => {
      const current = (localStorage.getItem("theme") as Theme) || "system";
      if (current === "system") {
        setTheme("system");
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    window.addEventListener("storage", onStorage);
    media.addEventListener("change", onSystemThemeChange);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("storage", onStorage);
      media.removeEventListener("change", onSystemThemeChange);
    };
  }, [open]);

  let themeIcon = faSun;
  if (theme === "dark") themeIcon = faMoon;
  else if (theme === "system") themeIcon = faCircleHalfStroke;

  const { t } = useTranslation();
  const { system, light, dark } = t("themes", { returnObjects: true }) || {
    system: "System",
    light: "Light",
    dark: "Dark",
  };

  return (
    <div className="relative z-10" ref={ref}>
      <button
        aria-label="Switch theme"
        className="peer hover:text-text-hover transition-colors cursor-pointer"
        onClick={() => setOpen((prev) => !prev)}
        onBlur={() => setOpen(false)}
      >
        <FontAwesomeIcon icon={themeIcon} className="text-xl" />
      </button>
      <div
        className={`flex flex-col items-center pb-3 absolute top-full left-1/2 -translate-x-1/2 transition-opacity duration-200 z-20 ${open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"} w-fit`}
      >
        {Themes.map((th) => (
          <button
            key={th}
            onClick={() => {
              applyTheme(th);
              setOpen(false);
            }}
            className={
              th === theme
                ? "[-webkit-text-stroke:1px_currentColor] hover:text-text/70 transition-colors whitespace-nowrap"
                : "hover:text-text-hover transition-colors whitespace-nowrap"
            }
            aria-label={`Switch to ${th}`}
          >
            {th === "system" ? system : th === "light" ? light : dark}
          </button>
        ))}
      </div>
    </div>
  );
}

export default Theme;
